import React, { useEffect, useState } from 'react';
import { Layout, message, Button, Space } from 'antd';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { selectUser, selectUserInitializing } from '../store/userSlice';
import { AppDispatch, RootState } from '../store';
import { fetchProjects, updateProject, createProject, deleteProject } from '../store/projectSlice';
import { Project } from '../types/project';
import ProjectSidebar from '../components/layout/ProjectSidebar';
import { ToolsView } from '../components/project/ToolsView';
import UpgradeModal from '../components/common/UpgradeModal';
import CreateProjectModal from '../components/common/CreateProjectModal';
import EditProjectModal from '../components/common/EditProjectModal';
import { ProjectStage } from '../constants/stages';
import { ChatView } from '../components/chat/ChatView';
import { ViewToggle } from '../components/layout/ViewToggle';
import WelcomeView from '../components/common/WelcomeView';
import StageOverview from '../components/project/StageOverview';
import NotificationHandler from '../components/common/Notification';

const PageContainer = styled(Layout)`
  min-height: 100vh;
  background: #f0f2f5;
`;

const MainLayout = styled(Layout)`
  height: 100vh;
  background: #f0f2f5;
`;

const ProjectDetailContainer = styled(Layout.Content)`
  padding: 0;
  background: #fff;
  margin: 0;
  height: 100vh;
  overflow-y: auto;
  border-radius: 0;
  box-shadow: none;
  position: relative;
  
  @media (max-width: 768px) {
    margin: 0;
    padding: 0;
    width: 100%;
    box-sizing: border-box;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
  position: relative;
  padding: 0;
  min-height: 100%;
  display: flex;
  flex-direction: column;
`;

const StageViewContainer = styled.div`
  flex: none;
  display: flex;
  flex-direction: column;
`;

const ToolsViewContainer = styled.div`
  flex: none;
  padding: 32px 0;
  background: #f8f9fa;
  border-top: 1px solid #eee;
`;

// 定义常量对象
const LOCAL_STORAGE_KEYS = {
  LAST_SELECTED_PROJECT: 'lastSelectedProjectId'
} as const;

const UPGRADE_TYPES = {
  PROJECT_LIMIT: 'PROJECT_LIMIT',
  STAGE_LOCKED: 'STAGE_LOCKED'
} as const;

const BREAKPOINTS = {
  MOBILE: 768
} as const;

const MainView: React.FC = (): JSX.Element => {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const user = useSelector(selectUser);
  const userInitializing = useSelector(selectUserInitializing);
  const [loading, setLoading] = useState(false);
  const [currentProject, setCurrentProject] = useState<Project | null>(null);
  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [isUpgradeModalVisible, setIsUpgradeModalVisible] = useState(false);
  const [collapsed, setCollapsed] = useState(window.innerWidth <= BREAKPOINTS.MOBILE);
  const [selectedStage, setSelectedStage] = useState<ProjectStage>(ProjectStage.PROBLEM_DEFINITION);
  const [upgradeType, setUpgradeType] = useState<keyof typeof UPGRADE_TYPES>(UPGRADE_TYPES.PROJECT_LIMIT);
  const [isGuestMode, setIsGuestMode] = useState(false);
  
  const projects = useSelector((state: RootState) => state.project.projects);

  useEffect(() => {
    if (userInitializing) {
      return;
    }
    
    if (user) {
      loadProjects();
    }
  }, [user, userInitializing]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= BREAKPOINTS.MOBILE) {
        setCollapsed(true);
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (projects.length > 0 && !currentProject) {
      const lastProjectId = localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_PROJECT);
      if (lastProjectId) {
        const lastProject = projects.find(p => p.id === lastProjectId);
        if (lastProject) {
          setCurrentProject(lastProject);
          return;
        }
      }
      // 如果没有缓存或缓存的项目不存在，选择第一个项目
      setCurrentProject(projects[0]);
    }
  }, [projects, currentProject]);

  useEffect(() => {
    if (currentProject) {
      localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_PROJECT, currentProject.id);
    }
  }, [currentProject]);

  useEffect(() => {
    if (currentProject) {
      setSelectedStage(currentProject.currentStage ?? ProjectStage.PROBLEM_DEFINITION);
    }
  }, [currentProject]);

  const loadProjects = async () => {
    try {
      await dispatch(fetchProjects()).unwrap();
    } catch (error: any) {
      console.error('获取项目列表失败:', error);
      message.error(error?.response?.data?.detail || '获取项目列表失败');
    }
  };

  const handleCreateClick = () => {
    if (!user) {
      navigate('/auth');
      return;
    }

    if (!user.isPremium && projects.length >= 1) {
      setUpgradeType(UPGRADE_TYPES.PROJECT_LIMIT);
      setIsUpgradeModalVisible(true);
      return;
    }
    setIsCreateModalVisible(true);
  };

  const handleCreateProject = async (values: { projectName: string; description: string }) => {
    if (!user?.isPremium && projects.length >= 1) {
      setIsCreateModalVisible(false);
      setUpgradeType(UPGRADE_TYPES.PROJECT_LIMIT);
      setIsUpgradeModalVisible(true);
      return;
    }

    try {
      setLoading(true);
      const projectData = {
        projectName: values.projectName,
        description: values.description || ''
      };
      console.log('创建项目数据:', projectData);
      const result = await dispatch(createProject(projectData)).unwrap();
      console.log('创建项目结果:', result);
      message.success('创建项目成功');
      setIsCreateModalVisible(false);
      
      // 设置新创建的项目为当前项目
      setCurrentProject(result);
    } catch (error: any) {
      console.error('创建项目失败:', error);
      // 优先使用后端返回的错误信息
      const errorMessage = error?.response?.data?.detail || error?.message || '创建项目失败，请稍后重试';
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditProject = async (values: { projectName: string; description: string }) => {
    if (!currentProject) return;
    
    try {
      setLoading(true);
      const projectData = {
        projectName: values.projectName,
        description: values.description
      };
      const result = await dispatch(updateProject({
        projectId: currentProject.id,
        projectData
      })).unwrap();
      
      // 更新当前选中的项目
      setCurrentProject(result);
      
      message.success('更新项目成功');
      setIsEditModalVisible(false);
    } catch (error: any) {
      console.error('更新项目失败:', error);
      message.error(error?.response?.data?.detail || '更新项目失败');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async () => {
    if (!currentProject) return;
    
    try {
      setLoading(true);
      await dispatch(deleteProject(currentProject.id)).unwrap();
      message.success('删除项目成功');
      setIsEditModalVisible(false);
      
      // 如果删除的是当前选中的项目，清除缓存
      if (currentProject.id === localStorage.getItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_PROJECT)) {
        localStorage.removeItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_PROJECT);
      }
      
      setCurrentProject(null);
    } catch (error: any) {
      console.error('删除项目失败:', error);
      message.error(error?.response?.data?.detail || '删除项目失败');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectClick = (project: Project) => {
    setCurrentProject(project);
    localStorage.setItem(LOCAL_STORAGE_KEYS.LAST_SELECTED_PROJECT, project.id);
  };

  const handleStepClick = async (index: ProjectStage) => {
    // 更新选中的阶段
    setSelectedStage(index);
    
    // 检查非会员项目数量限制
    if (!user?.isPremium && projects.length > 1) {
      setUpgradeType(UPGRADE_TYPES.PROJECT_LIMIT);
      setIsUpgradeModalVisible(true);
      return;
    }

    // 更新选中的阶段
    setSelectedStage(index);
  };

  const handleGuestMode = () => {
    setIsGuestMode(true);
  };

  return (
    <PageContainer>
      <NotificationHandler />
      <MainLayout>
        <ProjectSidebar
          projects={projects}
          currentProject={currentProject}
          selectedStage={selectedStage}
          collapsed={collapsed}
          onProjectSelect={handleProjectClick}
          onStageSelect={handleStepClick}
          onCreateClick={() => setIsCreateModalVisible(true)}
          onEditClick={() => setIsEditModalVisible(true)}
          onCollapse={setCollapsed}
        />
        <ProjectDetailContainer>
          <ContentWrapper>
            {(!user && !isGuestMode && selectedStage === undefined) ? (
              <WelcomeView onGuestMode={handleGuestMode} />
            ) : (
              <>
                <StageViewContainer>
                  <StageOverview 
                    stage={selectedStage}
                    currentProject={currentProject?.id || ''}
                  />
                </StageViewContainer>
                <ToolsViewContainer>
                  <ToolsView
                    currentProject={currentProject}
                    selectedStage={selectedStage}
                    onEditProject={() => setIsEditModalVisible(true)}
                    hideDescription
                    title="工具箱"
                  />
                </ToolsViewContainer>
              </>
            )}
          </ContentWrapper>
        </ProjectDetailContainer>
      </MainLayout>

      <CreateProjectModal
        open={isCreateModalVisible}
        onCancel={() => setIsCreateModalVisible(false)}
        onSubmit={handleCreateProject}
        loading={loading}
        projects={projects}
      />

      <EditProjectModal
        open={isEditModalVisible}
        onCancel={() => setIsEditModalVisible(false)}
        onSubmit={handleEditProject}
        onDelete={handleDeleteProject}
        loading={loading}
        currentProject={currentProject}
      />

      <UpgradeModal
        open={isUpgradeModalVisible}
        onCancel={() => setIsUpgradeModalVisible(false)}
        type={upgradeType}
      />
    </PageContainer>
  );
};

export default MainView; 